/**
 * WorkItemBoardCard — Kanban Board card.
 *
 * Represents a single work item on the kanban board.
 * Features:
 *   - 3px left accent strip via CSS variable --status-accent (set by parent column)
 *   - Whole-card drag activation (PointerSensor ≥5px separates click from drag)
 *   - GripVertical icon as visual affordance (hover-revealed)
 *   - Click anywhere on card → opens WorkItemDrawer
 *   - Type icon + label header (no UUID)
 *   - Badge row: priority, domain, sub-item count, knowledge link icon
 *   - Footer: owner avatar, due date, revision count
 *
 * Does NOT render the drawer itself — invokes onOpenDrawer() callback.
 * The isOverlay prop disables pointer events on the drag overlay clone.
 *
 * Fix-wave 1.1.3:
 *   - Fix 5: Type icon + capitalized label in header (replaces UUID)
 *   - Fix 6: Drag listeners on card root (whole-card drag)
 */

import React from 'react';
import { GripVertical, BookOpen, RefreshCw, Link2, CheckSquare, Package, Sparkles, Inbox } from 'lucide-react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Badge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { t } from '@/lib/i18n';
import { formatDate } from '@/lib/localeFormatters';
import { cn } from '@/lib/utils';
import type { RorosWorkItemDTO } from '@/types/roros/control-tower';

// ─── Helpers (shared with ControlTowerPage) ───────────────────────────────

function priorityVariant(p: number): 'error' | 'warning' | 'default' | 'success' | 'secondary' {
  switch (p) {
    case 1: return 'error';
    case 2: return 'warning';
    case 3: return 'default';
    case 4: return 'success';
    default: return 'secondary';
  }
}

function priorityLabel(p: number): string {
  const map: Record<number, string> = {
    1: t('Critical'),
    2: t('High'),
    3: t('Medium'),
    4: t('Low'),
    5: t('Lowest'),
  };
  return map[p] ?? String(p);
}

/** Returns a small icon for the work-item type (Fix 5). */
function typeIcon(type: string): React.ReactNode {
  switch (type) {
    case 'task':             return <CheckSquare className="h-3 w-3" />;
    case 'deliverable':      return <Package className="h-3 w-3" />;
    case 'improvement_item': return <Sparkles className="h-3 w-3" />;
    case 'intake_item':      return <Inbox className="h-3 w-3" />;
    default:                 return <CheckSquare className="h-3 w-3" />;
  }
}

/** Returns the display label for the work-item type (Fix 5). */
function typeLabel(type: string): string {
  switch (type) {
    case 'task':             return t('Task');
    case 'deliverable':      return t('Deliverable');
    case 'improvement_item': return t('Improvement');
    case 'intake_item':      return t('Intake');
    default:                 return type;
  }
}

// ─── WorkItemBoardCard ─────────────────────────────────────────────────────

interface WorkItemBoardCardProps {
  item: RorosWorkItemDTO;
  /** If true, the card is the drag overlay clone — pointer events disabled. */
  isOverlay?: boolean;
  /** Whether the current user has edit/transition permission. */
  canEdit: boolean;
  /** Called when the card body (not handle) is clicked. */
  onOpenDrawer: (item: RorosWorkItemDTO) => void;
  /** Sub-item count from rollup (0 if none). */
  subItemCount?: number;
  /** Actor name lookup. */
  actorName: (id: string | null) => string;
  /** Domain name lookup. */
  domainName: (id: string) => string;
}

export function WorkItemBoardCard({
  item,
  isOverlay = false,
  canEdit,
  onOpenDrawer,
  subItemCount = 0,
  actorName,
  domainName,
}: WorkItemBoardCardProps) {
  // Fix 6: spread attributes + listeners on the root card div, not just the grip button.
  // PointerSensor activationConstraint.distance=5 separates click (≤4px) from drag (≥5px).
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: item.id,
    data: { item },
    disabled: !canEdit || isOverlay,
  });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : 1,
  };

  const ownerName = actorName(item.ownerActorId);
  const domName = domainName(item.domainId);

  return (
    <div
      ref={setNodeRef}
      // Fix 6: drag listeners on whole card root; cursor-grab on hover when draggable
      {...(canEdit && !isOverlay ? attributes : {})}
      {...(canEdit && !isOverlay ? listeners : {})}
      className={cn(
        'group relative rounded-md border border-border bg-card shadow-sm shrink-0',
        // Left accent strip via inline border-l
        'border-l-[3px]',
        isOverlay && 'shadow-lg ring-2 ring-primary-300 pointer-events-none',
        !isOverlay && 'hover:shadow-md hover:border-border/80 transition-shadow duration-150',
        // Fix 6: whole-card cursor affordance
        canEdit && !isOverlay && !isDragging && 'cursor-grab',
        isDragging && 'cursor-grabbing',
      )}
      style={{
        ...style,
        borderLeftColor: 'var(--status-accent, var(--color-muted-foreground))',
      }}
      data-testid={`board-card-${item.id}`}
      aria-label={`${item.title} — ${t('drag to move')}`}
    >
      {/* Grip icon — visual affordance only (Fix 6: no longer the sole drag activator) */}
      {canEdit && !isOverlay && (
        <div
          className={cn(
            'absolute top-2 right-2 p-0.5 rounded text-muted-foreground/40',
            'opacity-0 group-hover:opacity-100 transition-opacity duration-150',
          )}
          aria-hidden="true"
          data-testid={`board-card-handle-${item.id}`}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </div>
      )}

      {/* Card body — clicking opens the drawer */}
      <div
        className="p-3 pl-3.5 select-none"
        tabIndex={0}
        role="button"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpenDrawer(item);
          }
        }}
        onClick={() => onOpenDrawer(item)}
      >
        {/* Fix 5: Type icon + capitalized label — replaces UUID header */}
        <div
          className="flex items-center gap-1 mb-1.5 text-muted-foreground/60"
          data-testid={`board-card-type-${item.id}`}
        >
          {typeIcon(item.type)}
          <span className="text-[11px] leading-none">
            {typeLabel(item.type)}
          </span>
        </div>

        {/* Title */}
        <p
          className="text-[13px] font-medium text-foreground line-clamp-2 mb-2 leading-snug"
          data-testid={`board-card-title-${item.id}`}
        >
          {item.title}
        </p>

        {/* Badge row */}
        <div className="flex flex-wrap items-center gap-1 mb-2">
          {item.priority <= 2 && (
            <Badge
              variant={priorityVariant(item.priority)}
              className="text-[10px] py-0 px-1.5 leading-tight"
            >
              {priorityLabel(item.priority)}
            </Badge>
          )}
          {domName && domName !== item.domainId && (
            <Badge
              variant="outline"
              className="text-[10px] py-0 px-1.5 leading-tight max-w-[80px] truncate"
            >
              {domName}
            </Badge>
          )}
          {subItemCount > 0 && (
            <span
              className="text-[10px] bg-muted text-muted-foreground rounded-full px-1.5 py-0.5 leading-none"
              title={`${subItemCount} ${t('sub-items')}`}
              data-testid={`board-card-subcount-${item.id}`}
            >
              {subItemCount}
            </span>
          )}
          {item.knowledgeItemId && (
            <span
              className="text-muted-foreground/60"
              title={t('Linked to knowledge item')}
            >
              <Link2 className="h-3 w-3" />
            </span>
          )}
        </div>

        {/* Footer row */}
        <div className="flex items-center justify-between gap-1 mt-1">
          <div className="flex items-center gap-1">
            {ownerName && ownerName !== '—' && (
              <Avatar
                size="xs"
                name={ownerName}
                aria-label={ownerName}
                title={ownerName}
              />
            )}
            {item.dueDate && (
              <span className="text-[10px] text-muted-foreground leading-none">
                {formatDate(item.dueDate)}
              </span>
            )}
          </div>
          {(item.revisionCount ?? 0) > 0 && (
            <span
              className="text-muted-foreground/50 flex items-center gap-0.5 text-[10px]"
              title={`${item.revisionCount} ${t('revisions')}`}
            >
              <RefreshCw className="h-2.5 w-2.5" />
              {item.revisionCount}
            </span>
          )}
          {item.escalationReason && (
            <span title={item.escalationReason}>
              <BookOpen className="h-2.5 w-2.5 text-error-500" />
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
